import { padZero } from "./String";

/**
 * Takes a string, hashes it to return a HEX color.
 *
 * For example, when passed "foo" it returns "#c68c01"
 *
 * If string is undefined then it returns #ffffff
 *
 * @source https://stackoverflow.com/a/16348977/1349766
 * @export
 * @param {string} str
 * @returns
 */
export function stringToColour(str: string): string {

    if (!str) {
        return "#ffffff";
    }

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let colour = "#";
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        colour += ("00" + value.toString(16)).substr(-2);
    }
    return colour;
}

/**
 * Inverts a HEX color.
 *
 * Provides a color that is on the opposite side of the color wheel.
 *
 * When passed true as the second parameter, it will invert the color
 * and provide it within grey scale to help with contrast and readability.
 *
 * @source https://stackoverflow.com/a/35970186
 * @export
 * @param {string} hex
 * @param {boolean} [bw]
 * @returns
 */
export function invertColor(hex: string, bw?: boolean): string {
    if (!hex) {
        return undefined;
    }

    if (hex.indexOf("#") === 0) {
        hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6) {
        throw new Error("Invalid HEX color.");
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if (bw) {
        // http://stackoverflow.com/a/3943023/112731
        return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000000" : "#FFFFFF";
    }
    // pad each with zeros and return
    return "#" + padZero((255 - r).toString(16)) + padZero((255 - g).toString(16)) + padZero((255 - b).toString(16));
}

/**
 * Return a darkened (by amount %) shade of the input color
 *
 * @param colorIn Input color (#... or rgb...)
 * @param amount % it should be darkened (negative = lighten)
 *
 * @returns
 */
export function darkenColor(colorIn: string | undefined, amount: number) {
    if (!colorIn) {
        return colorIn;
    }

    let r: number | undefined, g: number | undefined, b: number | undefined, a: number | undefined;
    let colorOut;

    try {
        // Parse the color format
        if (colorIn.startsWith('#')) {
            // Hex format
            const hexMatch = colorIn.match(/\w\w/g);
            if (hexMatch) {
                [r, g, b] = hexMatch.map(x => parseInt(x, 16));
            }
        } else if (colorIn.startsWith('rgb')) {
            // RGB or RGBA format
            const rgba = parseRgb(colorIn);
            if (rgba) {
                ({r, g, b, a} = rgba);
            }
        }

        if (r === undefined || g === undefined|| b === undefined) {
            console.error(`Unsupported color format: ${colorIn}`);
            return colorIn;
        }

        if (a !== undefined) {
            // Adjust luminance for RGBA
            const luminance = 0.3 * r + 0.59 * g + 0.11 * b;
            let newLuminance = luminance * (1 - amount / 100);
            newLuminance = Math.max(0, Math.min(1, newLuminance));  // within 0 and 1

            const ratio = newLuminance / (luminance || 1);  // no division by zero

            r = Math.round(r * ratio);
            g = Math.round(g * ratio);
            b = Math.round(b * ratio);

            // Adjust alpha
            a = a + amount / 100;
            a = Math.max(0, Math.min(1, a));  // within 0 and 1

            colorOut = `rgba(${r}, ${g}, ${b}, ${a})`;
        } else {
            // Adjust the color for RGB or Hex
            if (amount > 0) {
                r = Math.max(0, r - amount);
                g = Math.max(0, g - amount);
                b = Math.max(0, b - amount);
            } else {
                amount = Math.abs(amount);  // positive for lightening
                r = Math.min(255, r + amount);
                g = Math.min(255, g + amount);
                b = Math.min(255, b + amount);
            }

            colorOut = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }

    } catch (e) {
        console.error(`Unsupported color format: ${colorIn} - Error: ${e}`);
    }

    return colorOut;
}

function parseRgb(colorString: string): { r: number, g: number, b: number, a?: number } | null {
    const rgbaRegex = /^rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/;
    const rgbRegex = /^rgb?\((\d+),\s*(\d+),\s*(\d+)\)$/;

    let match: string[] | null;

    // eslint-disable-next-line no-cond-assign
    if (match = colorString.match(rgbaRegex)) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const a = parseFloat(match[4]);

        return { r, g, b, a };
    // eslint-disable-next-line no-cond-assign
    } else if (match = colorString.match(rgbRegex)) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);

        return { r, g, b };
    } else {
        return null;
    }
}